// MAP PICKUPS (owner-approved 2026-11-02, Vampire-Survivors-style treats on the larger arena). Pure + deterministic: no three,
// no DOM, and its own seeded Rng stream, so rolling loot never perturbs combat randomness. The Sim owns one FieldLoot and
// calls update() once per tick and onKill() for every death; render (src/render/fieldLoot.ts) and the HUD only read it.
//
//  - Relic caches: 3–6 per run at seeded spots spread over the map, away from the start. Walking into one opens it (a short
//    reveal), then grants a free draft pick; with a complete build it pays a gold-tier bonus instead (heal / shards / XP).
//  - Chests: ~30% of elites and every boss / guardian drop one. Opening it applies 1, 3 or 5 upgrades in order (bosses roll
//    better odds); the 'chestOpen' event carries the list so the UI can play a short reveal.
//  - Field items: rare, short-lived drops from any foe (about one every 1–2 minutes): Mend (heal), Lodestar (pull every XP
//    mote on the map), Sunburst (damage everything on screen; bosses take a fraction) and Fervor (+50% damage for 10 s).
import { PU, luckyOdds } from './powerups'; // POWER UPS Fortune Lens
import { Rng } from './util';
import { RELAXED_MEND } from './mods';
import type { Prop } from './terrain';
import type { Choice } from './sim';

export type FieldKind = 'mend' | 'vacuum' | 'clear' | 'fury';
export const FIELD_KINDS: readonly FieldKind[] = ['mend', 'vacuum', 'clear', 'fury'];

/** EDGE WALLS (owner 2026-11-02): every pickup rests at least this far (m) inside the arena wall, so the hero can reach it */
export const PICKUP_INSET = 1.5;

/** relic caches per run (inclusive) and where they may sit */
export const CACHE_MIN = 3, CACHE_MAX = 6;
/** nearest a cache may be to the start (m), how far inside the wall, and the spacing it tries to keep between caches */
export const CACHE_START_CLEAR = 45, CACHE_WALL_INSET = 14, CACHE_SPACING = 55;
/** touch radius (m) and the reveal before the reward lands (s) */
export const CACHE_TOUCH = 2.0, CACHE_REVEAL = 0.7;

/** chance an elite drops a chest (bosses / guardians always do) */
export const CHEST_ELITE_P = 0.3;
export const CHEST_TOUCH = 1.8;
/** upgrade-count odds [1, 3, 5] */
export const CHEST_ODDS: Record<'elite' | 'boss', [number, number, number]> = { elite: [0.72, 0.23, 0.05], boss: [0.25, 0.5, 0.25] };
export const CHEST_TIERS = [1, 3, 5] as const;

/** field items: no drop before FIELD_FIRST s or within FIELD_GAP s of the last one; after that each ordinary kill has
 *  FIELD_P to drop one. Measured over simulated runs this is about one item every 60–100 s (tests/fieldLoot.test.ts). */
export const FIELD_FIRST = 45, FIELD_GAP = 50, FIELD_P = 0.02;
/** seconds an item lies on the ground (it blinks for the last FIELD_BLINK s), touch radius */
export const FIELD_LIFE = 14, FIELD_BLINK = 4, FIELD_TOUCH = 1.6;
/** field items time out (off since 2026-11-03: they stay until picked up) */
export const FIELD_EXPIRES = false;
export const FIELD_WEIGHT: Record<FieldKind, number> = { mend: 30, vacuum: 25, clear: 20, fury: 25 };
/** Mend heals this share of max health */
export const MEND_HEAL = 0.35;
/** Sunburst: radius around the hero (about what the gameplay camera shows), elite / boss share of max health */
export const CLEAR_R = 26, CLEAR_ELITE = 0.5, CLEAR_BOSS = 0.08;
/** Fervor: damage multiplier and duration */
export const FURY_MUL = 1.5, FURY_TIME = 10;

export interface RelicCache { id: number; x: number; z: number; /** 0 closed, 1 opening (reveal), 2 open */ state: 0 | 1 | 2; t: number }
export interface Chest { alive: boolean; id: number; x: number; z: number; boss: boolean; age: number }
export interface FieldItem { alive: boolean; id: number; kind: FieldKind; x: number; z: number; life: number }
/** what a relic cache paid out */
export type CacheGrant = 'pick' | 'heal' | 'shards' | 'xp';

export type LootEvent =
  | { t: 'cacheOpen'; id: number; x: number; z: number }
  | { t: 'cacheGrant'; id: number; x: number; z: number; grant: CacheGrant; amount: number }
  | { t: 'chestDrop'; id: number; x: number; z: number; boss: boolean }
  /** n = upgrades applied (in order), picks = what they were, for the reveal */
  | { t: 'chestOpen'; id: number; x: number; z: number; boss: boolean; n: number; picks: Choice[] }
  | { t: 'fieldDrop'; id: number; kind: FieldKind; x: number; z: number }
  | { t: 'fieldPick'; id: number; kind: FieldKind; x: number; z: number }
  | { t: 'fieldExpire'; id: number; kind: FieldKind; x: number; z: number }
  /** Sunburst went off around (x, z) with radius r; n = foes hit */
  | { t: 'clearBurst'; x: number; z: number; r: number; n: number };

/** The slice of the Sim that loot needs (kept narrow so this file stays independent of sim.ts internals). */
export interface LootHost {
  readonly time: number;
  readonly state: string;
  readonly player: { x: number; z: number; hp: number; maxHp: number; xpNext: number };
  readonly opts: { readonly mods?: { readonly noheal?: boolean; readonly relaxed?: boolean } };
  /** POWER UPS Fortune Lens rank (0 / absent = the base odds) */
  readonly luck?: number;
  readonly events: { push(e: LootEvent): unknown };
  shards: number;
  gems: { alive: boolean; kind: number; pull: boolean }[];
  /** the current draft pool (every card a level-up could offer; no bonus / heal fillers) */
  draftPool(): Choice[];
  /** apply one draft card exactly like picking it at a level-up */
  applyChoice(c: Choice): void;
  /** queue a free draft (no level gained); opens now if the run is in play */
  grantPick(tag: string): void;
  /** SKILL FUSIONS: the gold FUSION card when a recipe is ready (both partners at max rank), else null */
  fusionCard(): Choice | null;
  addXp(v: number): void;
  /** Sunburst damage pass; returns foes hit */
  fieldBurst(x: number, z: number, r: number, eliteK: number, bossK: number): number;
}

/** gem kinds below this are XP motes (sim.ts GEM_SALVE) */
const XP_KINDS = 3;

export class FieldLoot {
  readonly caches: RelicCache[] = [];
  readonly chests: Chest[] = Array.from({ length: 12 }, () => ({ alive: false, id: 0, x: 0, z: 0, boss: false, age: 0 }));
  readonly items: FieldItem[] = Array.from({ length: 6 }, () => ({ alive: false, id: 0, kind: 'mend' as FieldKind, x: 0, z: 0, life: 0 }));
  /** Fervor seconds left */
  furyT = 0;
  /** run stats (QA / tests) */
  readonly stats = { chests: 0, chestUpgrades: 0, items: 0, picked: 0, caches: 0 };
  private rng: Rng;
  private nextId = 1;
  private lastField = -1e9;

  constructor(seed: number, readonly arenaR: number, props: readonly Prop[]) {
    this.rng = new Rng((seed * 92821 + 4099) >>> 0);
    this.placeCaches(props);
  }

  /** damage multiplier from Fervor */
  get dmgMul(): number { return this.furyT > 0 ? FURY_MUL : 1; }

  /** Spread the caches: random angle/distance (area-uniform) in the band, keeping CACHE_SPACING between them (relaxed if a
   *  layout is crowded) and clear of solid props so a cache never sits inside a boulder or spire. */
  private placeCaches(props: readonly Prop[]): void {
    const rng = this.rng, R = this.arenaR, n = CACHE_MIN + rng.int(CACHE_MAX - CACHE_MIN + 1);
    const r0 = CACHE_START_CLEAR, r1 = R - CACHE_WALL_INSET;
    let spacing = CACHE_SPACING;
    for (let tries = 0; this.caches.length < n && tries < 2000; tries++) {
      if (tries > 0 && tries % 150 === 0) spacing *= 0.85;
      const a = rng.range(0, Math.PI * 2), d = Math.sqrt(rng.range((r0 / R) ** 2, (r1 / R) ** 2)) * R;
      const x = Math.cos(a) * d, z = Math.sin(a) * d;
      if (this.caches.some((c) => (c.x - x) ** 2 + (c.z - z) ** 2 < spacing * spacing)) continue;
      if (props.some((p) => (p.x - x) ** 2 + (p.z - z) ** 2 < (p.r + (p.solid ? 2.5 : 1.2)) ** 2)) continue;
      this.caches.push({ id: this.nextId++, x, z, state: 0, t: 0 });
    }
  }

  /** every death (called by the Sim right after its kill event) */
  onKill(h: LootHost, x: number, z: number, elite: boolean, boss: boolean): void {
    const luck = h.luck ?? 0; // POWER UPS Fortune Lens: same rolls, better odds (determinism unchanged at rank 0)
    if (boss || (elite && this.rng.next() < CHEST_ELITE_P + PU.luckChest * luck)) { this.dropChest(h, x, z, boss); return; }
    if (elite) return;
    const t = h.time;
    if (t < FIELD_FIRST || t - this.lastField < FIELD_GAP) return;
    if (this.rng.next() >= FIELD_P * (1 + PU.luckField * luck)) return;
    const slot = this.items.find((q) => !q.alive); if (!slot) return;
    this.lastField = t;
    const kind = this.rollKind(h);
    ({ x, z } = this.within(x, z));
    slot.alive = true; slot.id = this.nextId++; slot.kind = kind; slot.x = x; slot.z = z; slot.life = FIELD_LIFE;
    this.stats.items++;
    h.events.push({ t: 'fieldDrop', id: slot.id, kind, x, z });
  }

  private rollKind(h: LootHost): FieldKind {
    const noHeal = !!h.opts.mods?.noheal, mendK = h.opts.mods?.relaxed ? RELAXED_MEND : 1, p = h.player;
    let total = 0;
    const rare = 1 + PU.luckField * (h.luck ?? 0); // Fortune Lens: the strong kinds (Sunburst, Fervor) come up more often
    const w = (k: FieldKind) => k === 'mend' ? (noHeal ? 0 : (p.hp > p.maxHp * 0.9 ? FIELD_WEIGHT.mend * 0.4 : FIELD_WEIGHT.mend) * mendK) : k === 'clear' || k === 'fury' ? FIELD_WEIGHT[k] * rare : FIELD_WEIGHT[k];
    for (const k of FIELD_KINDS) total += w(k);
    let r = this.rng.next() * total;
    for (const k of FIELD_KINDS) { r -= w(k); if (r < 0) return k; }
    return 'fury';
  }

  private dropChest(h: LootHost, x: number, z: number, boss: boolean): void {
    let slot = this.chests.find((c) => !c.alive);
    if (!slot) { slot = this.chests.reduce((a, b) => (a.age > b.age ? a : b)); } // full: the oldest makes room (never more than 12 waiting)
    // keep inside the wall so it can always be reached
    ({ x, z } = this.within(x, z));
    slot.alive = true; slot.id = this.nextId++; slot.x = x; slot.z = z; slot.boss = boss; slot.age = 0;
    h.events.push({ t: 'chestDrop', id: slot.id, x, z, boss });
  }

  /** (x, z) pulled in to the pickup radius (arenaR - PICKUP_INSET) */
  private within(x: number, z: number): { x: number; z: number } {
    const d = Math.hypot(x, z), lim = this.arenaR - PICKUP_INSET;
    return d > lim ? { x: x * lim / d, z: z * lim / d } : { x, z };
  }

  update(h: LootHost, dt: number): void {
    const p = h.player, playing = h.state === 'playing';
    // EDGE WALLS: anything resting past the pickup radius (an older rule, a QA drop) is set back inside
    for (const c of this.chests) if (c.alive) { const q = this.within(c.x, c.z); c.x = q.x; c.z = q.z; }
    for (const it of this.items) if (it.alive) { const q = this.within(it.x, it.z); it.x = q.x; it.z = q.z; }
    if (this.furyT > 0) this.furyT = Math.max(0, this.furyT - dt);
    for (const c of this.caches) {
      if (c.state === 1) {
        c.t += dt;
        if (c.t >= CACHE_REVEAL && playing) { c.state = 2; c.t = 0; this.payCache(h, c); }
      } else if (c.state === 0 && playing && (p.x - c.x) ** 2 + (p.z - c.z) ** 2 < CACHE_TOUCH * CACHE_TOUCH) {
        c.state = 1; c.t = 0; h.events.push({ t: 'cacheOpen', id: c.id, x: c.x, z: c.z });
      }
    }
    for (const c of this.chests) {
      if (!c.alive) continue;
      c.age += dt;
      if (playing && (p.x - c.x) ** 2 + (p.z - c.z) ** 2 < CHEST_TOUCH * CHEST_TOUCH) this.openChest(h, c);
    }
    for (const it of this.items) {
      if (!it.alive) continue;
      // owner 2026-11-03: 'power-up items dropped mid-game should not disappear' — field items no longer expire (no blink,
      // no fieldExpire); they wait until picked up (the 6 slots still cap how many lie on the field at once)
      if (FIELD_EXPIRES) { it.life -= dt; if (it.life <= 0) { it.alive = false; h.events.push({ t: 'fieldExpire', id: it.id, kind: it.kind, x: it.x, z: it.z }); continue; } }
      if (playing && (p.x - it.x) ** 2 + (p.z - it.z) ** 2 < FIELD_TOUCH * FIELD_TOUCH) this.useItem(h, it);
    }
  }

  private payCache(h: LootHost, c: RelicCache): void {
    this.stats.caches++;
    const p = h.player;
    // a ready fusion counts as a pick too: the free draft opens with the FUSION card (take it or skip it)
    if (h.draftPool().length || h.fusionCard()) { h.events.push({ t: 'cacheGrant', id: c.id, x: c.x, z: c.z, grant: 'pick', amount: 1 }); h.grantPick('relic'); return; }
    // complete build: a gold-tier bonus instead (low health → a big heal, else shards or an XP burst)
    const minute = Math.floor(h.time / 60), noHeal = !!h.opts.mods?.noheal;
    if (!noHeal && p.hp < p.maxHp * 0.5) {
      const amt = Math.round(p.maxHp * 0.6); p.hp = Math.min(p.maxHp, p.hp + amt);
      h.events.push({ t: 'cacheGrant', id: c.id, x: c.x, z: c.z, grant: 'heal', amount: amt });
    } else if (this.rng.next() < 0.5) {
      const amt = 30 + minute * 5; h.shards += amt;
      h.events.push({ t: 'cacheGrant', id: c.id, x: c.x, z: c.z, grant: 'shards', amount: amt });
    } else {
      const amt = Math.round(p.xpNext * 0.75);
      h.events.push({ t: 'cacheGrant', id: c.id, x: c.x, z: c.z, grant: 'xp', amount: amt });
      h.addXp(amt);
    }
  }

  /** roll the tier, then apply that many upgrades one after another (each from the pool as it stands after the last).
   *  SKILL FUSIONS (owner: "the next level-up OR CHEST offers a gold FUSION card the player can take or skip"): while a
   *  recipe is ready, one slot becomes the FUSION offer instead of an auto-applied upgrade; after the reveal a free draft
   *  opens led by the FUSION card, so the player takes it or skips it (skipping still takes another card from that draft). */
  private openChest(h: LootHost, c: Chest): void {
    c.alive = false;
    const odds = luckyOdds(CHEST_ODDS[c.boss ? 'boss' : 'elite'], h.luck ?? 0); // Fortune Lens
    let r = this.rng.next(), tier = 0;
    while (tier < 2 && r >= odds[tier]) { r -= odds[tier]; tier++; }
    const n = CHEST_TIERS[tier], picks: Choice[] = [];
    let fusion = false;
    for (let k = 0; k < n; k++) {
      if (!fusion) { const fc = h.fusionCard(); if (fc) { fusion = true; picks.push(fc); continue; } }
      const pick = this.pickUpgrade(h.draftPool());
      if (pick) { h.applyChoice(pick); picks.push(pick); continue; }
      // complete build: each remaining slot pays shards
      const amt = 12 + Math.floor(h.time / 60) * 2; h.shards += amt;
      picks.push({ kind: 'bonus', id: 'chest:shards', name: 'Shard Hoard', desc: `+${amt} ◆ Fracture Shards`, level: 0 });
    }
    this.stats.chests++; this.stats.chestUpgrades += n;
    h.events.push({ t: 'chestOpen', id: c.id, x: c.x, z: c.z, boss: c.boss, n, picks });
    if (fusion) h.grantPick('fusion'); // after every other upgrade landed, so the draft is rolled on the final build
  }

  /** chests favour what the build already has (evolutions first), then new cards */
  private pickUpgrade(pool: Choice[]): Choice | null {
    if (!pool.length) return null;
    const evo = pool.find((c) => c.kind === 'evolve'); if (evo) return evo;
    const w = (c: Choice) => (c.level > 1 ? (c.kind === 'weapon' ? 3 : 2) : 1);
    let total = 0; for (const c of pool) total += w(c);
    let r = this.rng.next() * total;
    for (const c of pool) { r -= w(c); if (r < 0) return c; }
    return pool[pool.length - 1];
  }

  private useItem(h: LootHost, it: FieldItem): void {
    it.alive = false; this.stats.picked++;
    const p = h.player;
    h.events.push({ t: 'fieldPick', id: it.id, kind: it.kind, x: it.x, z: it.z });
    switch (it.kind) {
      case 'mend': if (!h.opts.mods?.noheal) p.hp = Math.min(p.maxHp, p.hp + p.maxHp * MEND_HEAL); break;
      case 'vacuum': for (const g of h.gems) if (g.alive && g.kind < XP_KINDS) g.pull = true; break;
      case 'clear': { const n = h.fieldBurst(p.x, p.z, CLEAR_R, CLEAR_ELITE, CLEAR_BOSS); h.events.push({ t: 'clearBurst', x: p.x, z: p.z, r: CLEAR_R, n }); break; }
      case 'fury': this.furyT = FURY_TIME; break;
    }
  }

  /** QA staging (render checks / captures): drop a chest or an item at a spot without a kill */
  qaChest(h: LootHost, x: number, z: number, boss: boolean): void { this.dropChest(h, x, z, boss); }
  qaItem(h: LootHost, kind: FieldKind, x: number, z: number): void {
    const slot = this.items.find((q) => !q.alive) ?? this.items[0];
    ({ x, z } = this.within(x, z));
    slot.alive = true; slot.id = this.nextId++; slot.kind = kind; slot.x = x; slot.z = z; slot.life = FIELD_LIFE;
    h.events.push({ t: 'fieldDrop', id: slot.id, kind, x, z });
  }
}
